import * as Tone from 'tone';
import * as THREE from 'three';

class AudioManager {
    constructor() {
        this.isStarted = false;


    }

    start() {
        if (!this.isStarted) {
            Tone.start();
            this.isStarted = true;
            console.log('Audio context started');

            // Master output
            this.masterVolume = new Tone.Volume(-8).toDestination();

            // --- Engine Sound Components (No Change) ---
            this.engineOscillator = new Tone.PulseOscillator({ frequency: 60, width: 0.5 }).start();
            this.subOscillator = new Tone.Oscillator({ frequency: 30, type: 'square' }).start();
            this.engineDistortion = new Tone.Distortion(0.2);
            this.engineFilter = new Tone.Filter(600, "lowpass");
            this.engineVolume = new Tone.Volume(-12);
            this.engineOscillator.connect(this.engineDistortion);
            this.subOscillator.connect(this.engineDistortion);
            this.engineDistortion.chain(this.engineFilter, this.engineVolume, this.masterVolume);

            // --- Reverse Gear Whine (No Change) ---
            this.reverseWhine = new Tone.Oscillator({ frequency: 240, type: 'sine', volume: -Infinity }).start();
            this.reverseWhine.connect(this.masterVolume);

            // --- Tire Screech / Slip Sound (MODIFIED) ---
            this.slipNoise = new Tone.Noise('white').start();
            // Lowered base frequency from 6000 to 4500 for a less piercing sound.
            this.slipFilter = new Tone.Filter(1000, 'bandpass');
            this.slipFilter.Q.value = 8; // Slightly lower Q for a less "whistling" tone.
            this.slipVolume = new Tone.Volume(-Infinity);
            this.slipNoise.chain(this.slipFilter, this.slipVolume, this.masterVolume);
        }


    }

    update(throttle, forwardSpeed, wheelSlip) { // The signature is now simpler
        if (!this.isStarted) return;

        // --- Define the speed threshold for any slip sounds ---
        const MIN_SPEED_FOR_SLIP_SOUND = 5.0; // 5 m/s is about 18 km/h or 11 mph

        const absThrottle = Math.abs(throttle);

        // --- Engine Sound Logic (No Change) ---
        const idleFrequency = 40, maxFrequency = 100;
        const speedFactor = THREE.MathUtils.clamp(Math.abs(forwardSpeed) / 20, 0, 1);
        const loadFactor = absThrottle * (1 - speedFactor * 0.75);
        const targetFrequency = idleFrequency + (maxFrequency - idleFrequency) * (speedFactor * 0.6 + loadFactor * 0.4);
        this.engineOscillator.frequency.rampTo(targetFrequency, 0.1);
        this.subOscillator.frequency.rampTo(targetFrequency / 2, 0.1);
        const idleVolume = -18, maxVolume = -8;
        const targetEngineVolume = idleVolume + (maxVolume - idleVolume) * (absThrottle * 0.5 + speedFactor * 0.5);
        this.engineVolume.volume.rampTo(targetEngineVolume, 0.1);


        // --- Tire Slipping/Skidding Sound Logic (MODIFIED) ---
        // The sound now only triggers if BOTH conditions are met:
        // 1. There is significant wheel slip (from braking or sliding).
        // 2. The car is moving faster than the minimum speed threshold.
        if (wheelSlip > 0.3 && Math.abs(forwardSpeed) > MIN_SPEED_FOR_SLIP_SOUND) {
            const minSlipVolume = -40;
            const maxSlipVolume = -10;
            const speedInfluence = THREE.MathUtils.clamp(Math.abs(forwardSpeed) / 25, 0, 1);
            const targetSlipVolume = minSlipVolume + (maxSlipVolume - minSlipVolume) * Math.pow(wheelSlip, 1.2) * speedInfluence;
            this.slipVolume.volume.rampTo(targetSlipVolume, 0.05);

            const baseFreq = 4500;
            const freqVariation = (Math.abs(forwardSpeed) * 100) + (wheelSlip * 1000);
            const filterFrequency = baseFreq + freqVariation;
            this.slipFilter.frequency.rampTo(THREE.MathUtils.clamp(filterFrequency, 3000, 8500), 0.05);
        } else {
            // If conditions aren't met, fade the sound out.
            this.slipVolume.volume.rampTo(-Infinity, 0.4);
            this.slipFilter.frequency.rampTo(4500, 0.4);
        }
    }

    dispose() {
        if (!this.isStarted) return;
        [
            this.engineOscillator,
            this.subOscillator,
            this.reverseWhine,
            this.slipNoise,
        ].forEach((source) => {
            if (source && typeof source.stop === 'function') {
                try { source.stop(); } catch (e) { /* already stopped */ }
            }
        });
        [
            this.engineOscillator,
            this.subOscillator,
            this.reverseWhine,
            this.slipNoise,
            this.engineDistortion,
            this.engineFilter,
            this.engineVolume,
            this.slipFilter,
            this.slipVolume,
            this.masterVolume,
        ].forEach((node) => {
            if (node && typeof node.dispose === 'function') node.dispose();
        });
        this.isStarted = false;
    }
}

export default AudioManager;